import { ReactElement, useEffect, useMemo, useState } from 'react';
import Header from 'containers/Header';
import { RfUiBreadcrumbs, RfUiButton, RfUiModal, RfUiMultiselect } from 'renfe-components';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  getAllUsuarios,
  insertarUsuarioPerfil,
  eliminarUsuarioPerfil,
  habilitarUsuarioPerfil,
} from 'store/usuarios/actions';
import { makeSelectAllUsuarios } from 'store/usuarios/selectors';
import { makeSelectUsername } from 'store/app/selectors';
import { getSociedadesRequest } from 'store/practicas/actions';
import { makeSelectSociedadesParse } from 'store/practicas/selectors';
import './styles.css';
import {useCan} from 'hooks/visibilidadComponentes';

/* ============= Utils ============= */
const arrayToAPI = (arr: any[]) => (arr?.map((x: any) => x.value).join(',') || '');
type Option = { label: string; value: any };

const normalizeUsuariosToOptions = (raw: any): Option[] => {
  try {
    if (typeof raw === 'string') raw = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!Array.isArray(raw)) return [];

  // IDs bloqueados: usuarios con AL MENOS un 711 ACTIVO = 'S'
  const blockedIds = new Set<string>();
  for (const u of raw) {
    if (String(u?.ID_PERFIL) === '711' && String(u?.ACTIVO) === 'S') {
      blockedIds.add(String(u?.ID_USUARIO));
    }
  }

  // Normalizar sin bloqueados y sin duplicados
  const seenIds = new Set<string>();
  const result: Option[] = [];

  for (const u of raw) {
    const id = u?.ID_USUARIO;
    const nombre = u?.NOMBRE_USUARIO;
    if (!id || !nombre) continue;

    const idStr = String(id);

    if (blockedIds.has(idStr)) continue;
    if (seenIds.has(idStr)) continue;

    seenIds.add(idStr);
    result.push({ label: `${idStr} - ${nombre}`, value: idStr });
  }

  return result;
};

const normalizeUsuariosToOptions2 = (raw: any): Option[] => {
  try {
    if (typeof raw === 'string') raw = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!Array.isArray(raw)) return [];

  // IDs permitidos: usuarios con AL MENOS un perfil 711 ACTIVO = 'S'
  const allowedIds = new Set<string>();
  for (const u of raw) {
    if (String(u?.ID_PERFIL) === '711' && String(u?.ACTIVO) === 'S') {
      allowedIds.add(String(u?.ID_USUARIO));
    }
  }

  // Normalizar sin duplicados
  const seenIds = new Set<string>();
  const result: Option[] = [];

  for (const u of raw) {
    const id = u?.ID_USUARIO;
    const nombre = u?.NOMBRE_USUARIO;
    if (!id || !nombre) continue;

    const idStr = String(id);

    if (!allowedIds.has(idStr)) continue;
    if (seenIds.has(idStr)) continue;

    seenIds.add(idStr);
    result.push({ label: `${idStr} - ${nombre}`, value: idStr });
  }

  return result;
};

// Normaliza sociedades a options (label: "ID - NOMBRE", value: objeto sociedad)
const normalizeSociedadesToOptions = (raw: any): Array<{ label: string; value: any }> => {
  try {
    if (typeof raw === 'string') raw = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  return raw
    .map((s: any) => {
      const id = s?.ID_SOCIEDAD ?? s?.id ?? s?.value?.ID_SOCIEDAD;
      const nombre = s?.NOMBRE_SOCIEDAD ?? s?.nombre ?? s?.value?.NOMBRE_SOCIEDAD;
      if (!id || !nombre) return null;

      const idStr = String(id);
      if (seen.has(idStr)) return null;
      seen.add(idStr);

      return {
        label: `${idStr} - ${String(nombre)}`,
        value: s, // guardamos el objeto sociedad entero
      };
    })
    .filter(Boolean) as Array<{ label: string; value: any }>;
};


/* ============= solo para Alta Nuevo por campos ============= */
type AltaVisualizadorForm = { nombre: string; identificador: string; sociedadId: string };
async function existeEnRDA(id: string) {
  await new Promise((r) => setTimeout(r, 120));
  return id.trim().length > 0;
}
/* ========================================================================== */

const selectors = createStructuredSelector({
  usuariosAll: makeSelectAllUsuarios(),
  usuarioAltaBaja: makeSelectUsername(),
  sociedades: makeSelectSociedadesParse(),
} as any);

type AltaSelectorGroups = {
  idsToEnable: string[];
  idsToInsert: string[];
  idsAlreadyActive: string[];
  idsNotFound: string[];
};

const getIdsFromSelectedUsers = (selectedUsersJson: string): string[] => {
  const detail = JSON.parse(selectedUsersJson || '[]');
  const csv = arrayToAPI(detail);
  return (csv || '').split(',').map((x) => x.trim()).filter(Boolean);
};

const buildAltaSelectorGroups = (ids: string[], usuariosAll: any[]): AltaSelectorGroups => {
  const groups: AltaSelectorGroups = {
    idsToEnable: [],
    idsToInsert: [],
    idsAlreadyActive: [],
    idsNotFound: [],
  };

  for (const idUsuario of ids) {
    const rows = (usuariosAll || []).filter((x: any) => String(x?.ID_USUARIO) === String(idUsuario));

    if (!rows.length) {
      groups.idsNotFound.push(idUsuario);
    } else {
      addAltaSelectorGroup(idUsuario, rows, groups);
    }
  }

  return groups;
};

const addAltaSelectorGroup = (
  idUsuario: string,
  rows: any[],
  groups: AltaSelectorGroups
): void => {
  const has711ActivoS = rows.some((x: any) => String(x?.ID_PERFIL) === '711' && String(x?.ACTIVO) === 'S');
  const has711ActivoN = rows.some((x: any) => String(x?.ID_PERFIL) === '711' && String(x?.ACTIVO) === 'N');
  const hasAny711 = rows.some((x: any) => String(x?.ID_PERFIL) === '711');

  if (has711ActivoS) {
    groups.idsAlreadyActive.push(idUsuario);
  } else if (has711ActivoN) {
    groups.idsToEnable.push(idUsuario);
  } else if (!hasAny711) {
    groups.idsToInsert.push(idUsuario);
  } else {
    groups.idsAlreadyActive.push(idUsuario);
  }
};

const buildInsertPayload = (usuario: any, usuarioAltaBaja: any) => ({
  idUsuario: String(usuario.ID_USUARIO),
  nombreUsuario: String(usuario.NOMBRE_USUARIO ?? ''),
  idPerfil: '711',
  usuarioAlta: String(usuarioAltaBaja ?? ''),
  sociedadUsuario: String(usuario.SOCIEDAD_USUARIO ?? ''),
  tipoUsuario: String(usuario.TIPO_USUARIO ?? ''),
});

const buildAltaResultParts = (
  idsToEnable: string[],
  idsToInsert: string[],
  idsAlreadyActive: string[]
): string[] => {
  const parts: string[] = [];
  if (idsToEnable.length) parts.push(`Habilitar: ${idsToEnable.length}`);
  if (idsToInsert.length) parts.push(`Insertar: ${idsToInsert.length}`);
  if (idsAlreadyActive.length) parts.push(`Ya activos: ${idsAlreadyActive.length}`);
  return parts;
};

export default function AltaVisualizadores(): ReactElement {
  const dispatch = useDispatch();
  const { usuariosAll, usuarioAltaBaja, sociedades } = useSelector<any, any>(selectors);

  // Campos de Alta Nuevo
  const [form, setForm] = useState<AltaVisualizadorForm>({ nombre: '', identificador: '', sociedadId: '' });
  const [errors, setErrors] = useState<{
    nombre?: string;
    identificador?: string;
    sociedadId?: string;
    altaSel?: string;
    bajaSel?: string;
  }>({});
  const [saving, setSaving] = useState(false);

  // Modal
  const [modalType, setModalType] = useState<'success' | 'alert' | ''>('');
  const [modalMsg, setModalMsg] = useState<string>('');

  // Selectores ALTA / BAJA
  const usersOptionsAlta = useMemo(() => JSON.stringify(normalizeUsuariosToOptions(usuariosAll)), [usuariosAll]);
  const usersOptionsBaja = useMemo(() => JSON.stringify(normalizeUsuariosToOptions2(usuariosAll)), [usuariosAll]);
  const [selectedUserAltaUI, setSelectedUserAltaUI] = useState<string>('[]');
  const [selectedUserBajaUI, setSelectedUserBajaUI] = useState<string>('[]');
  const [usuariosBajaResetKey, setUsuariosBajaResetKey] = useState(0);
  const [usuariosAltaResetKey, setUsuariosAltaResetKey] = useState(0);

  // Selector SOCIEDADES (single)
  const sociedadesOptions = useMemo(() => JSON.stringify(normalizeSociedadesToOptions(sociedades)), [sociedades]);
  const [selectedSociedadUI, setSelectedSociedadUI] = useState<string>('[]');

  const links = useMemo(
    () => [{ label: 'Alta Visualizadores', link: '/altaVisualizadores', textHelp: 'Alta Visualizadores', active: true }],
    []
  );

  // Cargar usuarios y sociedades al montar
  useEffect(() => {
    dispatch(getAllUsuarios());
    dispatch(getSociedadesRequest() as any);
  }, [dispatch]);

  // Mantener margen sincronizado con el panel lateral
  useEffect(() => {
    const btn = document.querySelector('div.ui-lateral-menu > button.ui-lateral-menu__menu-button');
    const syncBodyClass = () => {
      if (!btn) return;
      const open = btn.classList.contains('ui-lateral-menu__menu-button--open');
      document.body.classList.toggle('left-menu-open', open);
    };
    let observer: MutationObserver | null = null;
    document.body.classList.add('left-menu-open');
    if (btn) {
      btn.addEventListener('click', syncBodyClass);
      observer = new MutationObserver(syncBodyClass);
      observer.observe(btn, { attributes: true, attributeFilter: ['class'] });
    }
    syncBodyClass();
    return () => {
      if (btn) btn.removeEventListener('click', syncBodyClass);
      if (observer) observer.disconnect();
      document.body.classList.remove('left-menu-open');
    };
  }, []);

  const clearSelectors = () => {
    setSelectedUserAltaUI('[]');
    setSelectedUserBajaUI('[]');
    setUsuariosBajaResetKey((k) => k + 1);
    setUsuariosAltaResetKey((k) => k + 1);
  };

  const abrirModal = (mensaje: string, tipo: 'success' | 'alert' = 'success') => {
    setModalMsg(mensaje);
    setModalType(tipo);
  };
  const cerrarModal = () => {
    setModalType('');
    setModalMsg('');
  };

  // ====== Selector Usuario ALTA ======
  const handleSelectedUserAlta = (e: any) => {
    const detail = e?.detail ?? [];
    const merged = mergeSelectedOptions(selectedUserAltaUI, detail);
    setSelectedUserAltaUI(JSON.stringify(merged));
    setErrors((p) => ({ ...p, altaSel: undefined, identificador: undefined }));
    if (!merged.length) {
      setUsuariosAltaResetKey((k) => k + 1);
    }
  };

  const canSee = useCan();
  // ====== Selector Usuario BAJA ======
  const handleSelectedUserBaja = (e: any) => {
    const detail = e?.detail ?? [];
    const merged = mergeSelectedOptions(selectedUserBajaUI, detail);
    setSelectedUserBajaUI(JSON.stringify(merged));
    setErrors((p) => ({ ...p, bajaSel: undefined }));
    if (!merged.length) {
      setUsuariosBajaResetKey((k) => k + 1);
    }
  };

  // ====== Selector Sociedad (SINGLE) ======
  const handleSelectedSociedad = (e: any) => {
    const detail = Array.isArray(e?.detail) ? e.detail : [];

    // Forzar SINGLE: quedarnos con la última selección
    const last = detail.length ? detail[detail.length - 1] : null;
    const singleArr = last ? [last] : [];

    setSelectedSociedadUI(JSON.stringify(singleArr));

    const v = singleArr[0]?.value;
    const idSoc =
      typeof v === 'string' ? v : String(v?.ID_SOCIEDAD ?? v?.value?.ID_SOCIEDAD ?? '');

    setForm((p) => ({ ...p, sociedadId: idSoc }));
    setErrors((p) => ({ ...p, sociedadId: undefined }));
  };

  // ====== Acciones selector ======
  const insertAltaSelectorUsers = (idsToInsert: string[]): boolean => {
    for (const idUsuario of idsToInsert) {
      const rows = (usuariosAll || []).filter((x: any) => String(x?.ID_USUARIO) === String(idUsuario));
      const payload = buildInsertPayload(rows[0], usuarioAltaBaja);

      if (!payload.nombreUsuario) {
        abrirModal(`Faltan datos obligatorios para el alta del usuario ${idUsuario} (nombreUsuario).`, 'alert');
        return false;
      }

      dispatch(insertarUsuarioPerfil(payload as any));
    }

    return true;
  };

  const onAltaFromSelector = () => {
    const ids = getIdsFromSelectedUsers(selectedUserAltaUI);

    if (!ids.length) {
      setErrors((p) => ({ ...p, altaSel: 'Seleccione usuario' }));
      abrirModal('Debe seleccionar al menos un usuario.', 'alert');
      return;
    }

    const uniqueIds = Array.from(new Set(ids));
    const { idsToEnable, idsToInsert, idsAlreadyActive, idsNotFound } = buildAltaSelectorGroups(
      uniqueIds,
      usuariosAll
    );

    if (idsNotFound.length) {
      abrirModal(`No se han encontrado en el listado estos usuarios: ${idsNotFound.join(', ')}`, 'alert');
      return;
    }

    if (idsToEnable.length) {
      dispatch(habilitarUsuarioPerfil({ idUsuarios: idsToEnable.join(','), idPerfil: '711' } as any));
    }

    if (!insertAltaSelectorUsers(idsToInsert)) {
      return;
    }

    const parts = buildAltaResultParts(idsToEnable, idsToInsert, idsAlreadyActive);

    clearSelectors();

    abrirModal(
      parts.length ? `Se han enviado las peticiones. (${parts.join(' · ')})` : 'No hay acciones a realizar.',
      'success'
    );
  };

  const onBajaFromSelector = () => {
    const detail = JSON.parse(selectedUserBajaUI || '[]');
    const csv = arrayToAPI(detail);
    const ids = (csv || '').split(',').map((x) => x.trim()).filter(Boolean);

    if (!ids.length) {
      setErrors((p) => ({ ...p, bajaSel: 'Seleccione usuario' }));
      abrirModal('Debe seleccionar al menos un usuario.', 'alert');
      return;
    }

    const uniqueIds = Array.from(new Set(ids));

    const idsNotFound: string[] = [];
    const idsToDelete: string[] = [];

    for (const idUsuario of uniqueIds) {
      const exists = (usuariosAll || []).some((x: any) => String(x?.ID_USUARIO) === String(idUsuario));
      if (!exists) idsNotFound.push(idUsuario);
      else idsToDelete.push(idUsuario);
    }

    if (idsNotFound.length) {
      abrirModal(`No se han encontrado en el listado estos usuarios: ${idsNotFound.join(', ')}`, 'alert');
      return;
    }

    if (idsToDelete.length) {
      dispatch(eliminarUsuarioPerfil({ idUsuarios: idsToDelete.join(','), idPerfil: '711' } as any));
    }

    const parts: string[] = [];
    if (idsToDelete.length) parts.push(`Baja: ${idsToDelete.length}`);

    clearSelectors();

    abrirModal(
      parts.length ? `Se han enviado las peticiones. (${parts.join(' · ')})` : 'No hay acciones a realizar.',
      'success'
    );
  };

  // ====== Alta Nuevo por campos ======
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: undefined }));
  };

  const limpiar = () => {
    setForm({ nombre: '', identificador: '', sociedadId: '' });
    setErrors({});
    clearSelectors();
    setSelectedSociedadUI('[]');
  };

  const validarAltaNuevo = (): boolean => {
    const n: typeof errors = {};
    if (!form.nombre.trim()) n.nombre = 'El nombre es obligatorio';
    if (!form.identificador.trim()) n.identificador = 'El identificador es obligatorio';
    if (!form.sociedadId.trim()) n.sociedadId = 'La sociedad es obligatoria';
    setErrors(n);
    return Object.keys(n).length === 0;
  };

  // Alta Nuevo
  const onSubmitAltaNuevo = async () => {
    if (!validarAltaNuevo()) return;

    setSaving(true);
    try {
      // si existe ya el usuario
      const exists = (usuariosAll || []).some((x: any) => String(x?.ID_USUARIO) === String(form.identificador ?? '').trim());

       if (exists) {
        abrirModal('Este usuario ya existe.', 'alert');
        return;
      }

      const payload = {
        idUsuario: String(form.identificador ?? '').trim(),
        nombreUsuario: String(form.nombre ?? '').trim(),
        idPerfil: '711',
        usuarioAlta: String(usuarioAltaBaja ?? ''),
        sociedadUsuario: String(form.sociedadId ?? '').trim(),
        tipoUsuario: 'INTERNO',
      };

      if (!payload.idUsuario || !payload.nombreUsuario || !payload.sociedadUsuario) {
        abrirModal('Faltan datos obligatorios para el alta.', 'alert');
        return;
      }

      dispatch(insertarUsuarioPerfil(payload as any));

      abrirModal('Se ha enviado la petición de ALTA del usuario.', 'success');
      limpiar();
    } catch {
      abrirModal('Ha ocurrido un error al dar de alta.', 'alert');
    } finally {
      setSaving(false);
    }
  };

    const parseArrayMaybeJson = (raw: any): any[] => {
    try {
      if (typeof raw === 'string') return JSON.parse(raw);
    } catch {
      return [];
    }
    return Array.isArray(raw) ? raw : [];
};

  const mergeSelectedOptions = (previousJson: string, currentDetail: any[]) => {
    const previous = parseArrayMaybeJson(previousJson);
    const current = Array.isArray(currentDetail) ? currentDetail : [];

    const map = new Map<string, any>();

    [...previous, ...current].forEach((item: any) => {
      const value = String(item?.value ?? '');
      if (value) map.set(value, item);
    });

    return Array.from(map.values());
};

  return (
    <>
      <Header />
      <div className="alta-visualizadores">
        <RfUiBreadcrumbs
          links={JSON.stringify(links)}
          onEventChange={(e: any) => { window.history.pushState({}, '', e.detail); }}
        />
        <h1 className="page-title">Alta Visualizadores</h1>

        <div className="rf-flex rf-gap-24 rf-flex-wrap selectors-row">
          <div className="selector-col form-group rf-flex-1">
            <RfUiMultiselect
              key={`alta-${usersOptionsAlta}-${usuariosAltaResetKey}`}
              options={usersOptionsAlta}
              defaultChecksRemake={selectedUserAltaUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione al menos una opción"
              text-label="Usuario"
              placeholder="Seleccione usuario"
              onOptionSelected={handleSelectedUserAlta}
            />
            {errors.altaSel && <div className="error">{errors.altaSel}</div>}
            {canSee.canSeePerfil ? (
              <div className="rf-flex rf-justify-center selectors-cta">
                <RfUiButton disabled={saving} text="Alta" text-help="Alta" onButtonClick={onAltaFromSelector} />
              </div>
            ) : null}
          </div>

          <div className="selector-col form-group rf-flex-1">
            <RfUiMultiselect
              key={`baja-${usersOptionsBaja}-${usuariosBajaResetKey}`}
              options={usersOptionsBaja}
              defaultChecksRemake={selectedUserBajaUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione al menos una opción"
              text-label="Visualiz."
              placeholder="Seleccione usuario"
              onOptionSelected={handleSelectedUserBaja}
            />
            {errors.bajaSel && <div className="error">{errors.bajaSel}</div>}
            {canSee.canSeePerfil ? (
              <div className="rf-flex rf-justify-center selectors-cta">
                <RfUiButton aspect="secondary" disabled={saving} text="Baja" text-help="Baja" onButtonClick={onBajaFromSelector} />
              </div>
            ) : null}
          </div>
        </div>

        <form className="form" onSubmit={(e) => { e.preventDefault(); onSubmitAltaNuevo(); }}>
          <div className="form-group">
            <label htmlFor="nombre" className="sr-only">Nombre usuario</label>
            <div className="input-prefixed">
              <span className="prefix">Nombre</span>
              <input id="nombre" name="nombre" value={form.nombre} onChange={onChange} placeholder="Introduzca nombre" />
            </div>
            {errors.nombre && <div className="error">{errors.nombre}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="identificador" className="sr-only">Identificador usuario</label>
            <div className="input-prefixed">
              <span className="prefix">Identificador</span>
              <input id="identificador" name="identificador" value={form.identificador} onChange={onChange} placeholder="Introduzca identificador" />
            </div>
            {errors.identificador && <div className="error">{errors.identificador}</div>}
          </div>

          {/* Selector Sociedad (SINGLE) */}
          <div className="form-group sociedad-single">
            <RfUiMultiselect
              key={`sociedades-${sociedadesOptions}`}
              options={sociedadesOptions}
              defaultChecksRemake={selectedSociedadUI}
              withoutOptionsText="Sin opciones"
              textError="Seleccione una opción"
              text-label="Sociedad"
              placeholder="Seleccione sociedad"
              onOptionSelected={handleSelectedSociedad}
            />
            {errors.sociedadId && <div className="error">{errors.sociedadId}</div>}
          </div>

          <div className="actions">
            <RfUiButton
              aspect="secondary"
              text="Limpiar filtro"
              text-help="Limpiar filtro"
              onButtonClick={() => limpiar()}
            />
            <div className="actions-right">
              {canSee.canSeePerfil ? (
                <RfUiButton
                  disabled={saving}
                  text={saving ? 'Guardando…' : 'Alta Nuevo'}
                  text-help="Alta Nuevo"
                  onButtonClick={() => onSubmitAltaNuevo()}
                />
              ) : null}
            </div>
          </div>
        </form>
      </div>

      {/* Modal */}
      <RfUiModal
        title-modal={modalMsg}
        id-modal="modalAltaVisualizador"
        modal-type={modalType}
        modalConfirmText="Aceptar"
        onModalClose={() => cerrarModal()}
        onConfirm={() => cerrarModal()}
      />
    </>
  );
}
